import * as Sentry from '@sentry/react';
import { serializeError } from 'serialize-error';
import { logger } from 'utils/logger';
import { store } from 'store/store';
import { sdkVirtualBackgroundChangedEventRequested } from 'features/sdk/actions';
import { virtualBackgroundInitialized } from 'features/virtual-backgrounds/virtualBackgroundsSlice';
import { SelectedVirtualBackground } from 'features/virtual-backgrounds/types';
import { selectVbProviderV2Enabled } from 'features/virtual-backgrounds/selectors';
import {
  ActivateBackgroundOptions,
  InputMediaOptions,
  VirtualBackgroundProvider,
} from './VirtualBackgroundProvider';
import { FigmentProvider } from './FigmentProvider';
import { EffectsSDKProvider } from './EffectsSDKProvider';
import { VBGateAbortedError, VBGateRefusedError } from './gateErrors';
import { trackVirtualBackgroundApplied, trackVirtualBackgroundRemoved } from './services';

interface ActiveBackgroundState {
  type: string;
  value: string;
}

const VB_LOG = { system: true, capture: 'virtual-background' } as const;

interface CancellationToken {
  cancelled: boolean;
}

export class VirtualBackgroundManagerClass {
  private _provider: VirtualBackgroundProvider | null = null;

  private _initPromise: Promise<void> | null = null;

  private _operationLock: Promise<void> = Promise.resolve();

  private _currentToken: CancellationToken | null = null;

  private _activated = false;

  /**
   * Tracks the most-recent failed activation. A subsequent successful activation
   * uses this to emit the retry-success info event with attemptCount/deltaMs/code.
   * Cleared on recovery, reset, deactivate, and destroy.
   */
  private _priorFailure: { code: number | undefined; failedAt: number; count: number } | null =
    null;

  private _activeBackground: ActiveBackgroundState = { type: '', value: '' };

  /** Cloned video track passed to the provider. Manager owns its lifecycle. */
  private _inputVideoTrack: MediaStreamTrack | null = null;

  private _releaseInputTrack(): void {
    this._inputVideoTrack?.stop();
    this._inputVideoTrack = null;
  }

  /**
   * Clone the source video track for provider input, stop the source, and
   * return a video-only stream. Sets `_inputVideoTrack` to the new clone.
   */
  private _prepareInputTrack(stream: MediaStream): MediaStream {
    const videoTrack = stream.getVideoTracks()[0];
    this._inputVideoTrack = videoTrack?.clone() ?? null;
    videoTrack?.stop();

    const videoOnlyStream = new MediaStream();
    if (this._inputVideoTrack) {
      videoOnlyStream.addTrack(this._inputVideoTrack);
    }

    return videoOnlyStream;
  }

  /** Compose the VB output stream — video track only. */
  private _composeOutputStream(processedStream: MediaStream): MediaStream {
    const resultStream = new MediaStream();
    const processedVideoTrack = processedStream.getVideoTracks()[0];
    if (processedVideoTrack) {
      resultStream.addTrack(processedVideoTrack);
    }

    return resultStream;
  }

  get activated() {
    return this._activated;
  }

  get activeBackground() {
    return this._activeBackground;
  }

  get initialized() {
    return !!this._provider?.initialized;
  }

  /** Override point for testing */
  private _createProvider(): VirtualBackgroundProvider {
    const v2Enabled = selectVbProviderV2Enabled(store.getState());

    return v2Enabled ? new EffectsSDKProvider() : new FigmentProvider();
  }

  private _reportInitFailure(error: unknown): void {
    // A refusal is recorded by the gate itself; an abort is teardown, not a failure.
    if (error instanceof VBGateRefusedError || error instanceof VBGateAbortedError) {
      return;
    }
    const provider = this._provider?.name ?? 'unknown';
    logger.remote(VB_LOG).error('[VB] Init failed', { provider, error: serializeError(error) });
    Sentry.captureException(error, {
      tags: { component: 'VirtualBackgroundManager', operation: 'init' },
      extra: { provider },
    });
  }

  ensureInitialized = async (): Promise<void> => {
    if (!this._provider) {
      this._provider = this._createProvider();
    }

    if (!this._initPromise) {
      this._initPromise = this._provider.init().catch((error) => {
        this._initPromise = null;
        this._reportInitFailure(error);
        throw error;
      });
    }

    await this._initPromise;
    store.dispatch(virtualBackgroundInitialized());
  };

  preload = async (): Promise<void> => this.ensureInitialized();

  activateBackground = async (
    options: ActivateBackgroundOptions & { type: string; value: string }
  ): Promise<MediaStream | void> => {
    if (this._activated) {
      return undefined;
    }

    return this._withLock(async (token) => {
      if (this._activated) {
        return undefined;
      }

      await this.ensureInitialized();
      if (token.cancelled) {
        return undefined;
      }

      const videoOnlyStream = this._prepareInputTrack(options.stream);

      let processedStream: MediaStream | void;
      try {
        processedStream = await this._provider!.activate({
          type: options.type,
          value: options.value,
          stream: videoOnlyStream,
        });
      } catch (error) {
        this._releaseInputTrack();
        const code = (error as { code?: number })?.code;
        const data = (error as { data?: unknown })?.data;
        Sentry.captureException(error, {
          tags: { component: 'VirtualBackgroundManager', operation: 'activate' },
          extra: { provider: this._provider!.name, backgroundType: options.type, code, data },
          fingerprint: typeof code === 'number' ? ['{{ default }}', String(code)] : undefined,
        });
        logger.remote(VB_LOG).error('[VB] Activation failed', {
          provider: this._provider!.name,
          backgroundType: options.type,
          error: serializeError(error),
        });
        this._priorFailure = this._priorFailure
          ? { ...this._priorFailure, count: this._priorFailure.count + 1 }
          : { code, failedAt: Date.now(), count: 1 };
        throw error;
      }

      if (token.cancelled) {
        if (processedStream) {
          await this._provider!.deactivate();
        }
        this._releaseInputTrack();
        return undefined;
      }

      if (!processedStream) {
        this._releaseInputTrack();
        logger.remote(VB_LOG).error('[VB] Provider activation returned no stream', {
          provider: this._provider!.name,
          backgroundType: options.type,
        });
        return undefined;
      }

      const resultStream = this._composeOutputStream(processedStream);

      this._activated = true;
      this._activeBackground = { type: options.type, value: options.value };

      trackVirtualBackgroundApplied(this._provider!.name);

      // Dispatch SDK event
      store.dispatch(
        sdkVirtualBackgroundChangedEventRequested({
          type: options.type,
          value: options.value,
        })
      );

      logger
        .remote(VB_LOG)
        .info('[VB] Activated', { provider: this._provider!.name, backgroundType: options.type });

      if (this._priorFailure) {
        const { code: failureCode, failedAt, count } = this._priorFailure;

        logger.remote(VB_LOG).info('[VB] Activation succeeded after prior failure', {
          provider: this._provider!.name,
          backgroundType: options.type,
          code: failureCode,
          attemptCount: count + 1,
          deltaMs: Date.now() - failedAt,
        });

        this._priorFailure = null;
      }

      return resultStream;
    });
  };

  changeBackground = async (config: SelectedVirtualBackground): Promise<void> =>
    this._withLock(async (token) => {
      if (!this._activated || !this._provider) {
        return;
      }
      if (token.cancelled) {
        return;
      }

      await this._provider.changeBackground(config);

      if (token.cancelled) {
        return;
      }

      this._activeBackground = { type: config.type, value: config.value };

      store.dispatch(sdkVirtualBackgroundChangedEventRequested(config));

      logger.remote(VB_LOG).info('[VB] Background changed', {
        provider: this._provider!.name,
        backgroundType: config.type,
      });
    });

  deactivate = async (): Promise<void> => {
    if (this._currentToken) {
      this._currentToken.cancelled = true;
    }

    return this._withLock(async () => {
      if (!this._provider || !this._activated) {
        return;
      }

      try {
        await this._provider.deactivate();
      } catch (error) {
        Sentry.captureException(error, {
          tags: { component: 'VirtualBackgroundManager', operation: 'deactivate' },
        });
      }

      this._releaseInputTrack();
      this._activated = false;
      this._activeBackground = { type: '', value: '' };
      this._priorFailure = null;

      trackVirtualBackgroundRemoved();

      logger.remote(VB_LOG).info('[VB] Deactivated');
    });
  };

  updateStream = async (options: InputMediaOptions): Promise<MediaStream | void> =>
    this._withLock(async (token) => {
      if (!this._activated || !this._provider) {
        return undefined;
      }
      if (token.cancelled) {
        return undefined;
      }

      const oldInputTrack = this._inputVideoTrack;
      const videoOnlyStream = this._prepareInputTrack(options.stream);

      let processedStream: MediaStream;
      try {
        processedStream = await this._provider.setInputMedia({
          stream: videoOnlyStream,
          stopTracksOnDestruction: options.stopTracksOnDestruction,
        });
      } catch (error) {
        this._releaseInputTrack();
        oldInputTrack?.stop();
        throw error;
      }

      // Safe to stop old track now that provider has switched to new input
      oldInputTrack?.stop();

      if (token.cancelled) {
        return undefined;
      }

      return this._composeOutputStream(processedStream);
    });

  reset = (): void => {
    if (this._currentToken) {
      this._currentToken.cancelled = true;
    }
    this._releaseInputTrack();
    this._provider?.reset();
    this._activated = false;
    this._activeBackground = { type: '', value: '' };
    this._priorFailure = null;

    logger.remote(VB_LOG).info('[VB] Reset', { provider: this._provider?.name ?? 'unknown' });
  };

  destroy = async (): Promise<void> => {
    if (this._currentToken) {
      this._currentToken.cancelled = true;
    }

    // Outside the lock on purpose: an in-flight init can hold the lock for ~30 s (the VB
    // re-probe window), so a cancellation waiting on that lock would arrive after the fact.
    this._provider?.abortInit();

    return this._withLock(async () => {
      if (this._provider) {
        try {
          await this._provider.destroy();
        } catch (error) {
          // A vendor destroy() throw at session teardown is benign — the provider's
          // try/finally still resets state, so there is no zombie reference or cascade.
          // Capture at warning level to keep frequency visibility without raising an error.
          Sentry.captureException(error, {
            level: 'warning',
            tags: { component: 'VirtualBackgroundManager', operation: 'destroy' },
          });
        }
      }

      this._provider = null;
      this._initPromise = null;
      this._releaseInputTrack();
      this._activated = false;
      this._activeBackground = { type: '', value: '' };
      this._priorFailure = null;
    });
  };

  setShowFps = (show: boolean): void => {
    this._provider?.setShowFps(show);
  };

  private async _withLock<T>(fn: (token: CancellationToken) => Promise<T>): Promise<T> {
    const token: CancellationToken = { cancelled: false };
    this._currentToken = token;

    const prev = this._operationLock;
    let resolve: () => void;
    this._operationLock = new Promise<void>((r) => {
      resolve = r;
    });

    await prev;

    try {
      return await fn(token);
    } finally {
      this._currentToken = null;
      resolve!();
    }
  }
}

export const VirtualBackgroundManager = new VirtualBackgroundManagerClass();
